"use client";

import { FileCode2, Files, Folder, GitBranch, Search } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

import { useSequence } from "../use-sequence";
import { AppWindow } from "../visual-kit";

// =============================================================================

type Token = [string, string];

// Syntax-highlighted lines (GitHub dark palette)
const KEYWORD = "#ff7b72";
const FUNCTION = "#d2a8ff";
const TAG = "#7ee787";
const ATTRIBUTE = "#79c0ff";
const STRING = "#a5d6ff";
const PLAIN = "#e6edf3";
const MUTED = "#8b949e";

const CODE: Token[][] = [
  [
    [KEYWORD, "import "],
    [PLAIN, "{ BookingButton } "],
    [KEYWORD, "from "],
    [STRING, '"@/components/booking"'],
    [PLAIN, ";"],
  ],
  [],
  [
    [KEYWORD, "export default function "],
    [FUNCTION, "Home"],
    [PLAIN, "() {"],
  ],
  [
    [KEYWORD, "  return "],
    [PLAIN, "("],
  ],
  [
    [PLAIN, "    <"],
    [TAG, "main "],
    [ATTRIBUTE, "className"],
    [PLAIN, "="],
    [STRING, '"hero"'],
    [PLAIN, ">"],
  ],
  [
    [PLAIN, "      <"],
    [TAG, "h1"],
    [PLAIN, ">A taste of Paris</"],
    [TAG, "h1"],
    [PLAIN, ">"],
  ],
  [
    [PLAIN, "      <"],
    [TAG, "BookingButton "],
    [ATTRIBUTE, "variant"],
    [PLAIN, "="],
    [STRING, '"gold"'],
    [PLAIN, " />"],
  ],
  [
    [PLAIN, "    </"],
    [TAG, "main"],
    [PLAIN, ">"],
  ],
  [[PLAIN, "  );"]],
  [[PLAIN, "}"]],
];

// 0..9 code lines · 10 build · 11 compiled · 12 pages · 13 deploy · 14 ready · 15 toast
const DURATIONS = [
  ...CODE.map((line) => (line.length ? 260 : 120)),
  900,
  600,
  650,
  900,
  700,
  2800,
];
const FIRST_TERMINAL_STEP = CODE.length;

const FILES = ["layout.tsx", "page.tsx", "globals.css"];

/**
 * Step 6: the code gets written, built and deployed to production.
 */
export function DeployVisual({ active }: { active: boolean }) {
  const t = useTranslations("Home.process.visuals.deploy");
  const step = useSequence(active, DURATIONS);
  const visibleLines = Math.min(step + 1, CODE.length);
  const terminalStep = step - FIRST_TERMINAL_STEP;

  const terminal = [
    { show: terminalStep >= 0, text: "$ npm run build", color: PLAIN },
    {
      show: terminalStep >= 0,
      text: "▲ Next.js 16.3 (Turbopack)",
      color: MUTED,
    },
    { show: terminalStep >= 1, text: `✓ ${t("build")}`, color: TAG },
    { show: terminalStep >= 2, text: `✓ ${t("pages")} (12/12)`, color: TAG },
    { show: terminalStep >= 3, text: "$ vercel deploy --prod", color: PLAIN },
    {
      show: terminalStep >= 4,
      text: `✓ ${t("deployed")} → https://le-gourmet.fr`,
      color: TAG,
    },
  ];

  return (
    <AppWindow dark>
      <div className="flex size-full flex-col font-mono">
        {/* Title bar */}
        <div className="flex h-[3.2cqw] shrink-0 items-center gap-[1.2cqw] border-b border-white/10 bg-[#161b22] px-[1.2cqw]">
          <span className="flex gap-[0.5cqw]">
            {["#ff5f57", "#febc2e", "#28c840"].map((color) => (
              <span
                key={color}
                className="size-[0.85cqw] rounded-full"
                style={{ background: color }}
              />
            ))}
          </span>
          <span className="flex h-full items-end gap-[0.3cqw] text-[0.95cqw]">
            {["page.tsx", "layout.tsx"].map((file, index) => (
              <span
                key={file}
                className={cn(
                  "rounded-t-[0.5cqw] px-[1cqw] py-[0.5cqw]",
                  index === 0 ? "bg-[#0d1117] text-white" : "text-white/45"
                )}
              >
                {file}
              </span>
            ))}
          </span>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Activity bar + explorer */}
          <div className="flex w-[3.2cqw] shrink-0 flex-col items-center gap-[1.2cqw] border-r border-white/10 bg-[#161b22] pt-[1.2cqw] text-white/45">
            <Files className="size-[1.4cqw] text-white/85" />
            <Search className="size-[1.4cqw]" />
            <GitBranch className="size-[1.4cqw]" />
          </div>
          <div className="w-[17%] shrink-0 border-r border-white/10 bg-[#0f141a] py-[1cqw] text-[0.9cqw] text-white/70">
            <p className="flex items-center gap-[0.5cqw] px-[1cqw] py-[0.3cqw]">
              <Folder className="size-[1cqw] text-[#79c0ff]" /> app
            </p>
            {FILES.map((file) => (
              <p
                key={file}
                className={cn(
                  "flex items-center gap-[0.5cqw] py-[0.3cqw] pr-[1cqw] pl-[2.2cqw]",
                  file === "page.tsx" && "bg-white/10 text-white"
                )}
              >
                <FileCode2 className="size-[1cqw] text-[#d2a8ff]" /> {file}
              </p>
            ))}
            <p className="flex items-center gap-[0.5cqw] px-[1cqw] py-[0.3cqw]">
              <Folder className="size-[1cqw] text-[#79c0ff]" /> components
            </p>
          </div>

          {/* Editor + terminal */}
          <div className="relative flex min-w-0 flex-1 flex-col">
            <div className="flex-1 overflow-hidden px-[1.2cqw] py-[1.2cqw] text-[1.05cqw] leading-[1.75]">
              {CODE.slice(0, visibleLines).map((line, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.18 }}
                  className="flex whitespace-pre"
                >
                  <span className="w-[2.4cqw] shrink-0 text-right text-white/25 select-none">
                    {index + 1}
                  </span>
                  <span className="pl-[1.4cqw]">
                    {line.map(([color, text], tokenIndex) => (
                      <span key={tokenIndex} style={{ color }}>
                        {text}
                      </span>
                    ))}
                    {index === visibleLines - 1 && terminalStep < 0 && (
                      <span className="ml-[0.1em] inline-block h-[1.1em] w-[0.12em] translate-y-[0.2em] animate-caret bg-white/80" />
                    )}
                  </span>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="absolute inset-x-0 bottom-0 h-[44%] border-t border-white/10 bg-[#0b0e13] px-[1.4cqw] py-[1cqw] text-[1cqw] leading-[1.7]"
              initial={false}
              animate={{ y: terminalStep >= 0 ? "0%" : "102%" }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="mb-[0.4cqw] text-[0.85cqw] tracking-wide text-white/40 uppercase">
                {t("terminal")}
              </p>
              {terminal.map((line) =>
                line.show ? (
                  <motion.p
                    key={line.text}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ color: line.color }}
                  >
                    {line.text}
                  </motion.p>
                ) : null
              )}
            </motion.div>
          </div>
        </div>

        {/* Deployed toast */}
        <AnimatePresence>
          {terminalStep >= 5 && (
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 26 }}
              className="absolute top-[4.6cqw] right-[1.4cqw] flex items-center gap-[0.8cqw] rounded-[0.8cqw] bg-white px-[1.2cqw] py-[0.8cqw] font-sans text-[1.05cqw] font-semibold text-[#0d1117] shadow-lg"
            >
              <span className="text-[1.4cqw]">🚀</span>
              {t("toast")}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppWindow>
  );
}

// =============================================================================
